<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 col-md-12 col-sm-12 text-center">
          <div class="amej-title">
            <h1>Pay Fund</h1>
          </div>
        </div>
      </div>

      <!-- STEP 1 : payment form -->
      <div class="row justify-content-center">
        <div class="col-lg-7 col-md-10 col-sm-12">
          <div class="pay-card">
            <b-form @submit.stop.prevent="verifyRecipient">
              <div class="form-row-x">
                <label>Recipient Email *</label>
                <b-form-input
                  v-model.trim="form.to"
                  type="email"
                  placeholder="name@example.com"
                  :class="errors.to ? 'is-invalid' : ''"
                ></b-form-input>
                <span class="text-danger" v-if="errors.to">{{ errors.to }}</span>
              </div>

              <div class="form-row-x">
                <label>Amount (USD) *</label>
                <b-form-input
                  v-model="form.amount"
                  placeholder="0.00"
                  :class="errors.amount ? 'is-invalid' : ''"
                  oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                ></b-form-input>
                <span class="text-danger" v-if="errors.amount">{{ errors.amount }}</span>
              </div>

              <div class="form-row-x">
                <label>Remarks</label>
                <b-form-input
                  v-model.trim="form.remarks"
                  placeholder="What is this payment for?"
                ></b-form-input>
                <span class="text-danger" v-if="errors.remarks">{{ errors.remarks }}</span>
              </div>

              <div class="text-danger mb-2" v-if="formError">{{ formError }}</div>

              <b-button variant="primary" type="submit" :disabled="isChecking">
                <b-spinner small v-if="isChecking" class="mr-2"></b-spinner>
                {{ isChecking ? "Checking recipient..." : "Continue" }}
              </b-button>
              <router-link :to="{ name: 'transactions' }" class="ml-3 link-muted">
                Payment List
              </router-link>
            </b-form>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 2/3 : confirm recipient + details -->
    <b-modal id="pay-confirm-modal" centered title="Confirm Payment" :no-close-on-backdrop="true">
      <div class="recipient-box" v-if="recipient && recipient.email">
        <img class="avatar" :src="recipient.image" v-if="recipient.image" :alt="recipient.full_name" />
        <div>
          <div class="rname">{{ recipient.full_name }}</div>
          <div class="remail">{{ recipient.email }}</div>
        </div>
      </div>
      <div class="detail-row"><span>Amount</span><strong>$ {{ money(form.amount) }}</strong></div>
      <div class="detail-row"><span>Remarks</span><strong>{{ form.remarks || '-' }}</strong></div>
      <p class="note mt-2">A verification code will be emailed to you to authorise this payment.</p>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSending" @click="startPayment">
          <b-spinner small v-if="isSending" class="mr-1"></b-spinner>
          {{ isSending ? "Sending..." : "Confirm & Send Code" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Edit</b-button>
      </template>
    </b-modal>

    <!-- STEP 4/5 : OTP + password -->
    <b-modal id="pay-otp-modal" centered title="Authorise Payment" :no-close-on-backdrop="true">
      <p class="note">
        Enter the code sent to your email and your account password to complete the payment of
        <strong>$ {{ money(draft.amount) }}</strong> to <strong>{{ recipient.full_name || form.to }}</strong>.
      </p>
      <div class="form-row-x">
        <label>Verification Code *</label>
        <b-form-input
          v-model.trim="otp"
          maxlength="6"
          placeholder="6-digit code"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          :class="errors.otp ? 'is-invalid' : ''"
        ></b-form-input>
        <span class="text-danger" v-if="errors.otp">{{ errors.otp }}</span>
      </div>
      <div class="form-row-x">
        <label>Account Password *</label>
        <b-form-input
          v-model="password"
          type="password"
          placeholder="Your login password"
          :class="errors.password ? 'is-invalid' : ''"
        ></b-form-input>
        <span class="text-danger" v-if="errors.password">{{ errors.password }}</span>
      </div>
      <div class="text-danger" v-if="payError">{{ payError }}</div>
      <div class="mt-2">
        <a href="#" class="link-muted" v-if="resendCooldown === 0" @click.prevent="resendCode">Resend code</a>
        <span class="muted" v-else>Resend code in {{ resendCooldown }}s</span>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isConfirming" @click="confirmPayment">
          <b-spinner small v-if="isConfirming" class="mr-1"></b-spinner>
          {{ isConfirming ? "Processing..." : "Pay Now" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <!-- STEP 6 : success invoice -->
    <b-modal id="pay-success-modal" centered size="lg" title="Payment Successful" :no-close-on-backdrop="true">
      <div id="pay-invoice" class="invoice">
        <div class="inv-head">
          <img :src="logoSrc" class="inv-logo" alt="Amej Pay" />
          <div class="inv-title">Payment Receipt</div>
        </div>
        <div class="inv-amount">$ {{ money(receipt.amount) }}</div>
        <div class="inv-status">{{ receipt.message || 'Transaction successful' }}</div>
        <div class="inv-rows">
          <div class="detail-row"><span>Transaction ID</span><strong>{{ receipt.transaction }}</strong></div>
          <div class="detail-row"><span>Paid To</span><strong>{{ receipt.toName }}</strong></div>
          <div class="detail-row"><span>Recipient Email</span><strong>{{ receipt.toEmail }}</strong></div>
          <div class="detail-row"><span>Remarks</span><strong>{{ receipt.remarks || '-' }}</strong></div>
          <div class="detail-row"><span>Date</span><strong>{{ receipt.date }}</strong></div>
        </div>
      </div>
      <template #modal-footer>
        <b-button size="sm" variant="primary" @click="downloadReceiptPdf">
          <span class="material-icons align-middle ic">picture_as_pdf</span> Download
        </b-button>
        <b-button size="sm" variant="primary" @click="printReceipt">
          <span class="material-icons align-middle ic">print</span> Print
        </b-button>
        <b-button size="sm" variant="primary" @click="shareReceipt">
          <span class="material-icons align-middle ic">share</span> Share
        </b-button>
        <b-button size="sm" variant="secondary" :to="{ name: 'transactions' }">Payment List</b-button>
        <b-button size="sm" variant="secondary" @click="closeSuccess">Close</b-button>
      </template>
    </b-modal>

    <b-toast id="pay-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Payment Failed</strong>
      </div>
      <div><p>{{ payError }}</p></div>
    </b-toast>
    <b-toast id="pay-info-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
  </div>
</template>

<script>
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";
import { jsPDF } from "jspdf";
import logoUrl from "@/assets/amej-pay.png";

export default {
  name: "PayFund",
  components: { "app-header": AppHeader },
  data() {
    return {
      form: { to: "", amount: "", remarks: "" },
      errors: {},
      formError: "",
      payError: "",
      infoText: "",
      otp: "",
      password: "",
      isChecking: false,
      isSending: false,
      isConfirming: false,
      draft: { transaction: "", amount: 0 },
      receipt: { transaction: "", amount: 0, toName: "", toEmail: "", remarks: "", date: "", message: "" },
      resendCooldown: 0,
      cooldownTimer: null,
      logoSrc: logoUrl,
      valid_email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
    };
  },
  computed: {
    ...mapState(["recipient", "profile"]),
  },
  beforeDestroy() {
    this.stopCooldown();
  },
  methods: {
    ...mapActions([
      "lookupRecipient", "createUserPayment", "confirmUserPayment", "listUserPayments",
    ]),

    money(v) {
      const n = Number(v);
      return (isNaN(n) ? 0 : n).toFixed(2);
    },

    validateForm() {
      const e = {};
      if (!this.form.to) { e.to = "Recipient email is required."; }
      else if (!this.valid_email.test(this.form.to)) { e.to = "Enter a valid email address."; }
      const amt = Number(this.form.amount);
      if (!String(this.form.amount).trim()) { e.amount = "Amount is required."; }
      else if (isNaN(amt) || amt <= 0) { e.amount = "Amount must be greater than 0."; }
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    /** STEP 2 - verify the recipient exists before showing the confirm popup. */
    verifyRecipient() {
      this.formError = "";
      if (!this.validateForm()) { return; }
      this.isChecking = true;
      const vm = this;
      return this.lookupRecipient(this.form.to)
        .then(function (res) {
          if (!res || !res.ok) {
            vm.formError = (res && res.message) || "Could not verify this recipient.";
            return;
          }
          if (res.recipient && res.recipient.is_self) {
            vm.formError = "You cannot send a payment to your own account.";
            return;
          }
          vm.$bvModal.show("pay-confirm-modal");
        })
        .then(function () { vm.isChecking = false; })
        .catch(function () { vm.isChecking = false; });
    },

    /** STEP 4 - POST pay, which emails the OTP. */
    startPayment() {
      this.isSending = true;
      this.payError = "";
      const vm = this;
      return this.createUserPayment({
        amount: Number(this.form.amount),
        to: this.form.to,
        remarks: this.form.remarks || "",
      }).then(function (res) {
        if (res && res.ok) {
          vm.draft = { transaction: res.transaction, amount: res.amount };
          vm.otp = "";
          vm.password = "";
          vm.errors = {};
          vm.$bvModal.hide("pay-confirm-modal");
          vm.$bvModal.show("pay-otp-modal");
          vm.startCooldown();
        } else {
          vm.payError = (res && res.insufficientBalance)
            ? "You do not have enough balance for this payment."
            : ((res && res.message) || "Unable to start this payment.");
          vm.$bvModal.hide("pay-confirm-modal");
          vm.$bvToast.show("pay-error-toast");
        }
      }).then(function () { vm.isSending = false; })
        .catch(function () { vm.isSending = false; });
    },

    /** STEP 5 - PUT pay/confirm { transaction_id, otp, password } */
    confirmPayment() {
      this.payError = "";
      const e = {};
      if (!this.otp) { e.otp = "Verification code is required."; }
      else if (this.otp.length !== 6) { e.otp = "Enter the 6-digit code."; }
      if (!this.password) { e.password = "Account password is required."; }
      this.errors = e;
      if (Object.keys(e).length) { return; }

      this.isConfirming = true;
      const vm = this;
      return this.confirmUserPayment({
        transaction_id: this.draft.transaction,
        otp: this.otp,
        password: this.password,
      }).then(function (res) {
        if (res && res.ok) {
          vm.receipt = {
            transaction: vm.draft.transaction,
            amount: vm.draft.amount,
            toName: (vm.recipient && vm.recipient.full_name) || "",
            toEmail: (vm.recipient && vm.recipient.email) || vm.form.to,
            remarks: vm.form.remarks,
            date: new Date().toLocaleString("en-GB", {
              day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
            }),
            message: res.message,
          };
          vm.password = "";
          vm.stopCooldown();
          vm.$bvModal.hide("pay-otp-modal");
          vm.$bvModal.show("pay-success-modal");
          vm.listUserPayments();
          return;
        }
        // Failure paths kept distinct so the user knows what to do next.
        if (res && res.insufficientBalance) {
          vm.payError = "You do not have enough balance for this payment.";
        } else if (res && res.passwordError) {
          vm.payError = res.message || "That password is not correct.";
          vm.$set(vm.errors, "password", vm.payError);
        } else if (res && res.otpError) {
          vm.payError = res.message || "That code is invalid or has expired. You can resend it.";
          vm.$set(vm.errors, "otp", vm.payError);
        } else {
          vm.payError = (res && res.message) || "Unable to complete this payment.";
        }
      }).then(function () { vm.isConfirming = false; })
        .catch(function () {
          vm.isConfirming = false;
          vm.payError = "Unable to complete this payment. Please try again.";
        });
    },

    /** Re-issues the payment (and therefore a fresh OTP) for the same details. */
    resendCode() {
      if (this.resendCooldown > 0) { return; }
      const vm = this;
      this.payError = "";
      return this.createUserPayment({
        amount: Number(this.form.amount),
        to: this.form.to,
        remarks: this.form.remarks || "",
      }).then(function (res) {
        if (res && res.ok) {
          vm.draft = { transaction: res.transaction, amount: res.amount };
          vm.otp = "";
          vm.infoText = "A new verification code has been sent to your email.";
          vm.$bvToast.show("pay-info-toast");
        } else {
          vm.payError = (res && res.message) || "Unable to resend the code.";
        }
        vm.startCooldown();
      });
    },

    startCooldown() {
      this.stopCooldown();
      this.resendCooldown = 60;
      const vm = this;
      this.cooldownTimer = setInterval(function () {
        vm.resendCooldown = vm.resendCooldown - 1;
        if (vm.resendCooldown <= 0) { vm.stopCooldown(); }
      }, 1000);
    },
    stopCooldown() {
      if (this.cooldownTimer) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
      this.resendCooldown = 0;
    },

    closeSuccess() {
      this.$bvModal.hide("pay-success-modal");
      this.form = { to: "", amount: "", remarks: "" };
      this.otp = "";
      this.password = "";
      this.errors = {};
      this.draft = { transaction: "", amount: 0 };
    },

    receiptRows() {
      const r = this.receipt;
      return [
        { label: "Transaction ID", value: r.transaction },
        { label: "Paid To", value: r.toName },
        { label: "Recipient Email", value: r.toEmail },
        { label: "Remarks", value: r.remarks },
        { label: "Date", value: r.date },
      ];
    },

    loadLogo() {
      const src = this.logoSrc;
      return new Promise(function (resolve) {
        try {
          const img = new Image();
          img.onload = function () { resolve(img); };
          img.onerror = function () { resolve(null); };
          img.src = src;
        } catch (e) { resolve(null); }
      });
    },

    downloadReceiptPdf() {
      const vm = this;
      return this.loadLogo().then(function (logo) {
        try {
          const doc = new jsPDF("p", "pt", "a4");
          const pageW = doc.internal.pageSize.getWidth();
          const left = 48, right = pageW - 48;
          let y = 60;
          if (logo) {
            try { doc.addImage(logo, "PNG", left, 36, 74, Math.round(74 * (106 / 198))); } catch (e) { /* no logo */ }
          }
          doc.setFontSize(18); doc.setTextColor(20, 20, 20);
          doc.text("Payment Receipt", right, y, { align: "right" });
          y += 52;
          doc.setFontSize(22); doc.setTextColor(0, 102, 179);
          doc.text("$ " + vm.money(vm.receipt.amount), left, y);
          doc.setFontSize(11); doc.setTextColor(0, 168, 0);
          doc.text(vm.receipt.message || "Transaction successful", right, y, { align: "right" });
          y += 14;
          doc.setDrawColor(220, 220, 220); doc.line(left, y, right, y);
          const rows = vm.receiptRows();
          for (let i = 0; i < rows.length; i++) {
            if (!rows[i].value) { continue; }
            y += 26;
            if (y > 780) { doc.addPage(); y = 60; }
            doc.setFontSize(10); doc.setTextColor(110, 110, 110);
            doc.text(String(rows[i].label), left, y);
            doc.setTextColor(25, 25, 25);
            const lines = doc.splitTextToSize(String(rows[i].value), 300);
            doc.text(lines, right, y, { align: "right" });
            if (lines.length > 1) { y += (lines.length - 1) * 12; }
          }
          y = Math.min(y + 40, 800);
          doc.setFontSize(9); doc.setTextColor(150, 150, 150);
          doc.text("Generated on " + new Date().toLocaleString("en-GB") + " - Amej Pay", left, y);
          doc.save("payment-" + String(vm.receipt.transaction || "receipt").replace(/[^A-Za-z0-9_-]/g, "") + ".pdf");
        } catch (e) {
          vm.payError = "Could not generate the PDF.";
          vm.$bvToast.show("pay-error-toast");
        }
      });
    },

    printReceipt() {
      const node = document.getElementById("pay-invoice");
      if (!node) { return; }
      const win = window.open("", "_blank", "width=820,height=900");
      if (!win) { return; }
      win.document.write(
        '<html><head><title>Payment Receipt</title><style>' +
        'body{font-family:Poppins,Arial,sans-serif;padding:32px;color:#1b1b1b;}' +
        '.inv-logo{height:40px;}.inv-title{font-size:20px;font-weight:700;margin-top:8px;}' +
        '.inv-amount{font-size:28px;font-weight:700;color:#0066b3;margin:12px 0 4px;}' +
        '.inv-status{color:#00a800;margin-bottom:16px;}' +
        '.detail-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;}' +
        '.detail-row span{color:#6c757d;}' +
        '</style></head><body>' + node.innerHTML + '</body></html>'
      );
      win.document.close();
      win.focus();
      setTimeout(function () { win.print(); }, 350);
    },

    shareReceipt() {
      const r = this.receipt;
      const text =
        "Amej Pay receipt\nAmount: $" + this.money(r.amount) +
        "\nTo: " + (r.toName || "") + " (" + (r.toEmail || "") + ")" +
        "\nTransaction: " + r.transaction +
        "\nDate: " + r.date;
      const vm = this;
      if (navigator.share) {
        navigator.share({ title: "Amej Pay receipt", text: text }).catch(function () { /* dismissed */ });
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () {
          vm.infoText = "Receipt details copied to your clipboard.";
          vm.$bvToast.show("pay-info-toast");
        });
        return;
      }
      vm.infoText = "Sharing is not supported on this browser.";
      vm.$bvToast.show("pay-info-toast");
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../css/style.scss';

.pay-card {
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
  padding: 28px; margin-bottom: 40px;
}
.form-row-x { margin-bottom: 16px; }
.form-row-x label { display: block; font-size: 14px; color: #6c757d; margin-bottom: 6px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.muted { color: #6c757d; font-size: 13px; }
.link-muted { color: #0066b3; font-weight: 600; font-size: 14px; }
.note { font-size: 13px; color: #6c757d; }
.recipient-box {
  display: flex; align-items: center; gap: 12px;
  background: #f6f8fa; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px;
}
.avatar { width: 46px; height: 46px; border-radius: 50%; object-fit: cover; }
.rname { font-weight: 700; }
.remail { font-size: 13px; color: #6c757d; }
.detail-row {
  display: flex; justify-content: space-between; padding: 8px 2px;
  border-bottom: 1px solid #f2f4f6; font-size: 14px; gap: 16px;
}
.detail-row:last-child { border-bottom: none; }
.detail-row span { color: #6c757d; }
.detail-row strong { text-align: right; word-break: break-word; }
.invoice { padding: 6px 2px; }
.inv-head { display: flex; align-items: center; justify-content: space-between; }
.inv-logo { height: 38px; }
.inv-title { font-size: 18px; font-weight: 700; }
.inv-amount { font-size: 30px; font-weight: 700; color: #0066b3; margin-top: 14px; }
.inv-status { color: #00a800; font-weight: 600; margin-bottom: 14px; }
.ic { font-size: 16px; margin-right: 3px; }
</style>
